import { useCallback, useEffect, useState } from "react";

import { connectDevice, disconnectDevice, fetchConnections } from "./devices-api";
import type { DeviceConnection } from "./devices-types";

export interface UseDeviceConnectionsReturn {
  connections: DeviceConnection[];
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
  connect: (provider: string) => Promise<void>;
  disconnect: (provider: string) => Promise<void>;
}

export function useDeviceConnections(): UseDeviceConnectionsReturn {
  const [connections, setConnections] = useState<DeviceConnection[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadConnections = useCallback(async () => {
    try {
      const response = await fetchConnections();
      setConnections(response.data);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load device connections");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refresh = useCallback(() => {
    setIsLoading(true);
    void loadConnections();
  }, [loadConnections]);

  useEffect(() => {
    void loadConnections();
  }, [loadConnections]);

  // Check URL params for post-OAuth redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("connected")) {
      void loadConnections();
      // Clean up URL
      const url = new URL(window.location.href);
      url.searchParams.delete("connected");
      window.history.replaceState({}, "", url.toString());
    }
  }, [loadConnections]);

  const connect = useCallback(
    async (provider: string) => {
      try {
        const response = await connectDevice(provider);
        if ("auth_url" in response.data) {
          window.location.href = response.data.auth_url;
        } else {
          // Mock mode — just refresh connections
          await loadConnections();
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to connect device");
      }
    },
    [loadConnections]
  );

  const disconnectFn = useCallback(
    async (provider: string) => {
      try {
        await disconnectDevice(provider);
        await loadConnections();
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to disconnect device");
      }
    },
    [loadConnections]
  );

  return {
    connections,
    isLoading,
    error,
    refresh,
    connect,
    disconnect: disconnectFn,
  };
}
